<script setup lang="ts">
import {reactive, watch} from "vue";
import axios from "axios";
import {Button, Icon, InputText} from "@/Components/Library";
import {useDebounceFn} from "@vueuse/core";
import PageLoading from "./PageLoading.vue";

type Props = {
    placeholder?: string;
};

const props = withDefaults(defineProps<Props>(), {
    placeholder: "Start typing to search...",
});

const model = defineModel();

const emit = defineEmits(["selected"]);
const state = reactive<{
    images: {
        id: string;
        urls: {small: string};
        user: {name: string; links: {html: string}};
    }[];
    page: number;
    totalPages: number;
    searchTerm: string;
    loading: boolean;
    isTyping: boolean;
}>({
    images: [],
    page: 1,
    totalPages: 1,
    searchTerm: "",
    loading: false,
    isTyping: false,
});
const search = () => {
    if (!state.searchTerm) {
        state.images = [];
        state.images = [];
        state.page = 1;
        state.totalPages = 1;
        return;
    }
    console.log("search");
    state.isTyping = false;
    state.loading = true;
    //TODO refactor to try/catch and a service
    axios
        .get(route("unsplash.search", [state.searchTerm, state.page]), {})
        .then((resp) => {
            console.log(resp);
            console.log("resp data", resp.data);
            state.images = resp.data.results;
            state.totalPages = resp.data.totalPages;
        })
        .catch((errors) => {
            console.log(errors);
        })
        .finally(() => {
            state.loading = false;
        });
};
const clearSearch = () => {
    state.searchTerm = "";
    state.images = [];
    state.page = 1;
    state.totalPages = 1;
};
const changePage = (direction: "PREV" | "NEXT") => {
    if (direction === "PREV" && state.page > 1) {
        state.page--;
    } else if (direction === "NEXT" && state.page < state.totalPages) {
        state.page++;
    }
    search();
};
watch(
    () => state.searchTerm,
    useDebounceFn(() => search(), 500),
);

const selectImage = (image: {id: string}) => {
    if (state.selected?.id === image.id) {
        model.value = null;
        return;
    }

    model.value = image;
};

defineExpose({clearSearch});
</script>
<template>
    <div v-auto-animate>
        <div class="mb-2">
            <InputText
                id="stock_image_search"
                ignore="true"
                type="search"
                v-model="state.searchTerm"
                :loading="state.loading"
                clearable
                :placeholder="placeholder"
                fluid
                @input="state.isTyping = true"
                @keypress.enter.prevent.stop.native="search"
            />
        </div>
        <div class="mb-2 grid grid-cols-2 gap-2" v-if="state.images.length" v-auto-animate>
            <template v-for="image in state.images" :key="image.id">
                <button type="button" class="p--1" @click.stop="selectImage(image)" v-auto-animate="{duration: 100}">
                    <div v-if="state.loading">
                        <div class="flex aspect-square w-full items-center justify-center text-stone-200">
                            <Icon icon="line-md:loading-twotone-loop" height="80%" class="" />
                        </div>
                        <small class="text-no-wrap px-1"></small>
                    </div>
                    <div
                        v-else
                        class="relative rounded text-left"
                        :class="[model?.id === image.id ? 'bg-askr-green' : 'bg-stone-50 dark:bg-stone-800']"
                    >
                        <img
                            :class="[!!model && model?.id === image.id ? 'border-askr-green' : 'border-stone-50']"
                            :src="image.urls.thumb"
                            class="aspect-square w-full cursor-pointer rounded-t object-cover"
                            alt=""
                        />
                        <small class="text-no-wrap px-1 text-xs" :class="[{'text-white': model?.id === image.id}]">
                            By:
                            <a
                                :href="`${image.urls.regular}?utm_source=ASKrx&utm_medium=referral`"
                                class="text-decoration-none"
                                download
                            >
                                {{ image.user.name }}
                            </a>
                        </small>
                        <Icon
                            icon="material-symbols:check-circle"
                            v-if="model?.id === image.id"
                            class="absolute right-1 top-1 rounded-full bg-white text-askr-green shadow"
                            width="20"
                            height="20"
                        />
                        <a :href="route('media.unsplash.download', [image.id])" download class="absolute left-1 top-1">
                            <Button
                                @click.stop
                                type="button"
                                icon="mdi:download"
                                class="h-7 w-7 rounded-full bg-white text-askr-green shadow"
                            />
                        </a>
                    </div>
                </button>
            </template>
        </div>
        <div class="flex items-center justify-between" v-if="state.images.length">
            <Button
                type="button"
                text
                :disabled="state.loading"
                @click.stop="changePage('PREV')"
                class="font-meidum !py-1 !pl-0 text-xs"
            >
                <Icon icon="material-symbols:chevron-left-rounded" width="16" />
                PREV
            </Button>
            <span class="text-xs">Page {{ state.page }} of {{ state.totalPages }}</span>
            <Button
                type="button"
                text
                :disabled="state.loading"
                @click.stop="changePage('NEXT')"
                class="font-meidum !py-1 !pr-0 text-xs"
            >
                NEXT
                <Icon icon="material-symbols:chevron-right-rounded" width="16" />
            </Button>
        </div>
        <div v-else-if="state.loading">
            <PageLoading />
        </div>
    </div>
</template>
<style scoped>
.image {
    border: 3px solid transparent;
    margin: -3px;
}

.image.selected {
    border-color: #44ab89;
}
</style>
